// bundle-variant-handler.js
// Handles variant selection and modal content updates for bundle products

if (!customElements.get('bundle-variant-handler')) {
  customElements.define('bundle-variant-handler', class BundleVariantHandler extends HTMLElement {
    constructor() {
      super();
      this.bundleAddButton = this.querySelector('.add-this-bundle-item');
      this.productHandle = this.dataset.productHandle;
      this.variantSelects = this.querySelector('variant-selects');
      this.currentVariant = null;
      this.abortController = null;
      this.lastFocusedSelector = null;
      
      this.preProcessHtmlCallbacks = [this.preserveFocus.bind(this)];
      this.postProcessHtmlCallbacks = [this.restoreFocus.bind(this)];
      
      this.init();
    }

    init() {
      this.initCurrentVariant();
      
      if (this.variantSelects) {
        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );
      }
    }
    
    preserveFocus(newContent) {
      const activeElement = document.activeElement;
      
      if (activeElement && this.contains(activeElement)) {
        if (activeElement.tagName === 'SELECT') {
          const selectWrapper = activeElement.closest('.product-form__input');
          if (selectWrapper) {
            const optionName = selectWrapper.querySelector('label')?.textContent?.trim();
            this.lastFocusedSelector = { type: 'select', name: optionName, value: activeElement.value };
          }
        } else if (activeElement.tagName === 'INPUT' && activeElement.type === 'radio') {
          const fieldset = activeElement.closest('fieldset');
          if (fieldset) {
            const legend = fieldset.querySelector('legend')?.textContent?.trim();
            this.lastFocusedSelector = { type: 'radio', name: legend, value: activeElement.value };
          }
        } else if (activeElement.classList.contains('add-this-bundle-item')) {
          this.lastFocusedSelector = { type: 'button', isAddButton: true };
        }
      }
    }
    
    restoreFocus(newNode) {
      if (!this.lastFocusedSelector) return;
      
      requestAnimationFrame(() => {
        const { type, isAddButton, name, value } = this.lastFocusedSelector;
        
        if (type === 'button' && isAddButton) {
          newNode.querySelector('.add-this-bundle-item')?.focus();
        } else if (type === 'select') {
          newNode.querySelectorAll('select').forEach(select => {
            const wrapper = select.closest('.product-form__input');
            if (wrapper?.querySelector('label')?.textContent?.trim() === name) {
              select.focus();
            }
          });
        } else if (type === 'radio') {
          newNode.querySelectorAll('fieldset').forEach(fieldset => {
            if (fieldset.querySelector('legend')?.textContent?.trim() === name) {
              fieldset.querySelector(`input[value="${value}"]`)?.focus();
            }
          });
        }
        
        this.lastFocusedSelector = null;
      });
    }
    
    initCurrentVariant() {
      const variantScript = this.querySelector('[data-selected-variant]');
      if (variantScript) {
        try {
          this.currentVariant = JSON.parse(variantScript.innerHTML);
          this.updateButtonState();
        } catch (error) {
          console.error('Error parsing initial variant data:', error);
        }
      }
    }
    
    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      this.dataset.section = this.closest('.shopify-section')?.id.replace('shopify-section-', '') || 'bundle-modal';
    }
    
    disconnectedCallback() {
      this.onVariantChangeUnsubscriber?.();
      this.abortController?.abort();
    }
    
    handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
      if (!this.contains(event.target)) return;
      
      this.resetButtonState();
      const productUrl = this.dataset.url || `/products/${this.productHandle}`;
      
      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues),
        targetId: target.id,
        callback: this.handleUpdateVariantInfo.bind(this)
      });
    }
    
    buildRequestUrlWithParams(url, optionValues) {
      const params = [];
      if (optionValues?.length) params.push(`option_values=${optionValues.join(',')}`);
      params.push('view=product-bundle');
      return `${url}?${params.join('&')}`;
    }
    
    renderProductInfo({ requestUrl, targetId, callback }) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      
      fetch(requestUrl, { signal: this.abortController.signal })
        .then(response => {
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          return response.text();
        })
        .then(responseText => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          callback(html);
        })
        .catch(error => {
          if (error.name !== 'AbortError') {
            console.error('Error fetching variant data:', error);
          }
        });
    }
    
    handleUpdateVariantInfo(html) {
      const variant = this.getSelectedVariant(html);
      const isCorrectVariant = this.validateSelectedVariant(variant);
      
      if (!isCorrectVariant) {
        const correctVariant = this.findCorrectVariant(html);
        if (correctVariant) {
          this.handleVariantUpdate(html, correctVariant);
          return;
        }
      }
      
      this.handleVariantUpdate(html, variant);
      
      if (variant) {
        this.currentVariant = variant;
        this.updateButtonState();
      }
    }
    
    validateSelectedVariant(variant) {
      if (!variant) return false;
      
      const selectedValues = Array.from(this.querySelectorAll('variant-selects select'))
        .map(select => select.value)
        .filter(Boolean);
      
      const variantOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);
      
      return selectedValues.length === variantOptions.length && 
             selectedValues.every(value => variantOptions.includes(value));
    }
    
    findCorrectVariant(html) {
      const selectedValues = Array.from(this.querySelectorAll('variant-selects select'))
        .map(select => select.value)
        .filter(Boolean);
      
      const localVariantsScript = this.querySelector('[data-product-variants]');
      if (localVariantsScript) {
        try {
          const variants = JSON.parse(localVariantsScript.textContent);
          const matchingVariant = this.findMatchingVariantInList(variants, selectedValues);
          if (matchingVariant) return matchingVariant;
        } catch (error) {
          console.error('Error parsing local variants:', error);
        }
      }
      
      const scripts = html.querySelectorAll('script[type="application/json"]');
      for (const script of scripts) {
        try {
          const data = JSON.parse(script.textContent);
          
          if (Array.isArray(data)) {
            const matchingVariant = this.findMatchingVariantInList(data, selectedValues);
            if (matchingVariant) return matchingVariant;
          }
          
          if (data.variants && Array.isArray(data.variants)) {
            const matchingVariant = this.findMatchingVariantInList(data.variants, selectedValues);
            if (matchingVariant) return matchingVariant;
          }
          
          if (data.id && (data.option1 || data.option2 || data.option3)) {
            const variantOptions = [data.option1, data.option2, data.option3].filter(Boolean);
            if (this.arraysMatch(selectedValues, variantOptions)) return data;
          }
        } catch (error) {
          continue;
        }
      }
      
      return null;
    }
    
    findMatchingVariantInList(variants, selectedValues) {
      return variants.find(variant => {
        const variantOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);
        return this.arraysMatch(selectedValues, variantOptions);
      });
    }
    
    arraysMatch(arr1, arr2) {
      if (arr1.length !== arr2.length) return false;
      const sorted1 = [...arr1].sort();
      const sorted2 = [...arr2].sort();
      return sorted1.every((val, index) => val === sorted2[index]);
    }
    
    handleVariantUpdate(html, variant) {
      const newBundleContent = html.querySelector('.bundle-product-content');
      const currentBundleContent = this.querySelector('.bundle-product-content');
      
      if (newBundleContent && currentBundleContent && variant) {
        this.updateContentWithCorrectVariant(newBundleContent, variant);
        
        if (typeof HTMLUpdateUtility !== 'undefined' && HTMLUpdateUtility.viewTransition) {
          HTMLUpdateUtility.viewTransition(
            currentBundleContent,
            newBundleContent,
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        } else {
          this.preserveFocus(newBundleContent);
          currentBundleContent.innerHTML = newBundleContent.innerHTML;
          this.restoreFocus(currentBundleContent);
        }
        
        this.reinitializeAfterUpdate();
        this.forceUpdateButtonAttributes(variant);
        
        requestAnimationFrame(() => {
          this.attachButtonListener();
        });
      }
      
      this.updateVariantData(variant);
      
      publish(PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId: this.dataset.section || this.productHandle,
          html,
          variant
        }
      });
    }
    
    updateContentWithCorrectVariant(contentElement, variant) {
      const hiddenInput = contentElement.querySelector('input[name="id"]');
      if (hiddenInput) {
        hiddenInput.value = variant.id;
        hiddenInput.dataset.price = variant.price;
      }
      
      const variantScript = contentElement.querySelector('[data-selected-variant]');
      if (variantScript) variantScript.textContent = JSON.stringify(variant);
      
      const selects = contentElement.querySelectorAll('variant-selects select');
      const variantOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);
      
      selects.forEach((select, index) => {
        if (!variantOptions[index]) return;
        
        Array.from(select.options).forEach(option => option.removeAttribute('selected'));
        
        const correctOption = Array.from(select.options).find(option => option.value === variantOptions[index]);
        if (correctOption) {
          correctOption.setAttribute('selected', 'selected');
          select.value = variantOptions[index];
          
          const label = select.closest('.product-form__input')?.querySelector('.option-selected-value');
          if (label) label.textContent = variantOptions[index];
        }
      });
      
      const button = contentElement.querySelector('.add-this-bundle-item');
      if (button) {
        button.setAttribute('data-id', variant.id);
        button.dataset.id = variant.id;
        button.disabled = !variant.available;
        button.textContent = variant.available ? 'Add to Bundle' : (window.variantStrings?.soldOut || 'Sold Out');
      }
    }
    
    forceUpdateButtonAttributes(variant) {
      if (!variant) return;
      
      const button = this.querySelector('.add-this-bundle-item');
      if (!button) return;
      
      button.setAttribute('data-id', variant.id);
      button.dataset.id = variant.id;
      button.disabled = !variant.available;
      button.textContent = variant.available ? 'Add to Bundle' : (window.variantStrings?.soldOut || 'Sold Out');
      button.classList.toggle('disabled', !variant.available);
    }
    
    reinitializeAfterUpdate() {
      this.bundleAddButton = this.querySelector('.add-this-bundle-item');
      this.variantSelects = this.querySelector('variant-selects');
      
      const variantScript = this.querySelector('[data-selected-variant]');
      if (variantScript) {
        try {
          this.currentVariant = JSON.parse(variantScript.textContent);
          this.syncDropdownsWithVariant(this.currentVariant);
          
          if (this.bundleAddButton) {
            const buttonDataId = this.bundleAddButton.getAttribute('data-id');
            if (buttonDataId != this.currentVariant.id) {
              this.bundleAddButton.setAttribute('data-id', this.currentVariant.id);
              this.bundleAddButton.dataset.id = this.currentVariant.id;
            }
          }
        } catch (error) {
          console.error('Error parsing variant data after update:', error);
        }
      }
      
      if (this.variantSelects && !this.variantSelects.hasAttribute('data-initialized')) {
        this.variantSelects.setAttribute('data-initialized', 'true');
      }
    }
    
    syncDropdownsWithVariant(variant) {
      if (!variant) return;
      
      const variantOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);
      const selects = this.querySelectorAll('variant-selects select');
      
      selects.forEach((select, index) => {
        if (!variantOptions[index]) return;
        
        select.value = variantOptions[index];
        
        Array.from(select.options).forEach(option => {
          option.toggleAttribute('selected', option.value === variantOptions[index]);
        });
        
        const label = select.closest('.product-form__input')?.querySelector('.option-selected-value');
        if (label) label.textContent = variantOptions[index];
      });
    }
    
    getSelectedVariant(html) {
      const variantSelects = html.querySelector('variant-selects [data-selected-variant]');
      if (variantSelects) {
        try {
          return JSON.parse(variantSelects.innerHTML);
        } catch (error) {
          console.error('Error parsing variant data:', error);
        }
      }
      return null;
    }
    
    updateVariantData(variant) {
      this.currentVariant = variant;
      
      const variantScript = this.querySelector('[data-selected-variant]');
      if (variantScript && variant) {
        variantScript.textContent = JSON.stringify(variant);
      }
      
      const hiddenInput = this.querySelector('input[name="id"]');
      if (hiddenInput && variant) {
        hiddenInput.value = variant.id;
        hiddenInput.dataset.price = variant.price;
      }
      
      this.updateButtonState();
    }
    
    resetButtonState() {
      if (this.bundleAddButton) {
        this.bundleAddButton.disabled = true;
      }
    }
    
    updateButtonState() {
      if (!this.bundleAddButton) return;
      
      if (this.currentVariant) {
        this.bundleAddButton.disabled = !this.currentVariant.available;
        this.bundleAddButton.textContent = this.currentVariant.available 
          ? 'Add to Bundle' 
          : window.variantStrings.soldOut;
        this.bundleAddButton.dataset.id = this.currentVariant.id;
      } else {
        this.bundleAddButton.disabled = true;
        this.bundleAddButton.textContent = window.variantStrings.unavailable;
      }
    }
    
    attachButtonListener() {
      const button = this.querySelector('.add-this-bundle-item');
      if (!button) return;
      
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);
      this.bundleAddButton = newButton;
      
      newButton.addEventListener('click', (e) => {
        e.preventDefault();
        
        if (!this.currentVariant) {
          console.error('No variant selected');
          return;
        }
        
        document.dispatchEvent(new CustomEvent('bundle:addFromModal', {
          detail: { variant: this.currentVariant, productHandle: this.productHandle },
          bubbles: true
        }));
      });
    }
  });
}